/**
 * Jeton CSRF (`CSRF_HEADER` de `@flux/shared`) — `04` §6 : double soumission
 * signée. `POST /auth/login` le renvoie une fois dans le corps JSON ; il n'est
 * jamais posé dans un cookie (`http/auth/middleware.ts` côté serveur).
 *
 * Il est gardé en mémoire *et* dans `sessionStorage` : `GET /auth/me` (utilisé
 * par le garde d'authentification à chaque chargement de route protégée) ne
 * renvoie que `SessionUser` — pas de `csrfToken` (voir `SessionUser` vs.
 * `SessionResponse` dans `@flux/shared/dto/auth`). Sans ce report dans
 * `sessionStorage`, un simple F5 sur une route protégée laisserait
 * l'utilisateur authentifié (le cookie de session survit) mais incapable
 * d'effectuer la moindre mutation tant qu'il ne s'est pas reconnecté. C'est un
 * pis-aller : un nouvel onglet ouvert après redémarrage du navigateur perd
 * quand même le jeton tant que la session (12 h glissantes) n'est pas
 * renouvelée par une connexion — voir le rapport de vague.
 */
const STORAGE_KEY = 'flux.csrfToken';

let inMemoryToken: string | null = null;

function readStorage(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Stockage indisponible (navigation privée) : le jeton reste en mémoire
    // pour la durée de l'onglet, ce qui suffit pour la session en cours.
  }
}

export function setCsrfToken(token: string): void {
  inMemoryToken = token;
  writeStorage(token);
}

export function getCsrfToken(): string | null {
  inMemoryToken ??= readStorage();
  return inMemoryToken;
}

export function clearCsrfToken(): void {
  inMemoryToken = null;
  writeStorage(null);
}
